import * as Yup from 'yup';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
import { format, parse } from 'date-fns';
import { axios } from 'utils/axios';
import { AuthContext } from 'contexts/JWTContext';
import TicketSummary from 'components/transactions/TicketSummary';
import TicketTabs, { TicketTabConfig } from 'components/transactions/TicketTabs';
import TransactionDetailsTab from 'components/transactions/TransactionDetailsTab';
import WasteItemsTab from 'components/transactions/WasteItemsTab';
import TicketNotesTab from 'components/transactions/TicketNotesTab';
import {
  DriverNewFormProps,
  TicketSummaryData,
  WasteItemDetail
} from '../../../../@types/transactions';

export default function TransactionForm({ isEdit, currentTransaction }: DriverNewFormProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State for dropdowns
  const [haulier, setHaulier] = useState<any>([]);
  const [client, setClient] = useState<any>([]);
  const [drivers, setDrivers] = useState<any>([]);
  const [problems, setProblems] = useState<any>([]);
  const [vehicleReg, setVehicleReg] = useState<any>([]);
  const [bridgeIdentity, setBridgeIdentity] = useState<any>([]);
  const [currentDate, setCurrentDate] = useState<any>(
    new Date().toISOString().split('.')[0].slice(0, 16) + ':00Z'
  );
  const auth = useContext(AuthContext);
  const user = auth?.user;

  const summaryData = useMemo<TicketSummaryData>(() => {
    const dateValue =
      currentTransaction?.Weight1DateTime ||
      currentTransaction?.createdAt ||
      new Date().toISOString();

    return {
      ticketNumber: currentTransaction?.TicketNumber || 'IWS-50771',
      date: format(new Date(dateValue), 'dd/MM/yyyy'),
      netWeight: `${currentTransaction?.WeightNet ?? '0'} KG`,
      driver: currentTransaction?.DriverName || 'Unassigned Driver',
      vehicle: currentTransaction?.VehicleReg || 'Pending'
    };
  }, [currentTransaction]);

  // Map transactionItems from API to WasteItemDetail format
  const wasteItems = useMemo<WasteItemDetail[]>(() => {
    if (
      !currentTransaction?.transactionItems ||
      !Array.isArray(currentTransaction.transactionItems)
    ) {
      return [];
    }

    return currentTransaction.transactionItems.map((item: any, index: number) => {
      // Remove spaces from EWC code for display (e.g., "20 03 03" -> "200303")
      const ewcCode = item.EWC ? item.EWC.replace(/\s+/g, '') : '';

      return {
        id: item.Id?.toString() || `item-${index}`,
        title: item.Description || 'Unnamed Item',
        description: item.Description || '',
        code: ewcCode,
        containment: 'Bulk', // Default value as not provided in API
        quantity: item.ItemNetWeight || 0,
        hazardous: item.HAZ || false,
        sicCode: '', // Not provided in API, using empty string
        processing: {
          rdCode: '', // Not provided in API, using empty string
          route: '', // Not provided in API, using empty string
          destination:
            currentTransaction?.Destination || currentTransaction?.DestinationSiteName || ''
        }
      };
    });
  }, [currentTransaction]);

  const mockNotes = useMemo(
    () => [
      'Site inspection completed and documentation verified.',
      `Driver ${summaryData.driver} confirmed loading at 09:15.`,
      'Awaiting final approval from compliance team before dispatch.'
    ],
    [summaryData.driver]
  );

  // Helper functions for date/time handling
  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return { date: '', time: '' };
    const [date, time] = dateTimeString.split('T');
    // Convert from ISO format (YYYY-MM-DD) to UK format (DD-MM-YYYY)
    let ukDate = '';
    if (date) {
      try {
        const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
        ukDate = format(parsedDate, 'dd-MM-yyyy');
      } catch {
        ukDate = date; // Fallback to original if parsing fails
      }
    }
    return {
      date: ukDate,
      time: time ? time.slice(0, 5) : ''
    };
  };

  const combineDateTime = (date: string, time: string) => {
    if (!date || !time) return '';
    // Validate date format (DD-MM-YYYY) and convert to ISO (YYYY-MM-DD)
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    // Validate time format (HH:MM)
    const timeRegex = /^\d{2}:\d{2}$/;

    if (!dateRegex.test(date) || !timeRegex.test(time)) {
      return '';
    }

    // Convert from UK format (DD-MM-YYYY) to ISO format (YYYY-MM-DD)
    try {
      const parsedDate = parse(date, 'dd-MM-yyyy', new Date());
      const isoDate = format(parsedDate, 'yyyy-MM-dd');
      return `${isoDate}T${time}:00Z`;
    } catch {
      return '';
    }
  };

  // Validation functions
  const isValidDate = (dateString: string | undefined) => {
    if (!dateString) return false;
    // Validate UK format (DD-MM-YYYY)
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (!dateRegex.test(dateString)) return false;

    try {
      const [day, month, year] = dateString.split('-').map(Number);
      const date = parse(dateString, 'dd-MM-yyyy', new Date());
      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day &&
        day >= 1 &&
        day <= 31 &&
        month >= 1 &&
        month <= 12
      );
    } catch {
      return false;
    }
  };

  const isValidTime = (timeString: string | undefined) => {
    if (!timeString) return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(timeString);
  };

  const NewTransactionSchema = Yup.object().shape({
    TicketNumber: Yup.string().nullable().notRequired(),
    ClientId: Yup.number().required('Client ID is required'),
    Weight1Date: Yup.string()
      .required('Weight 1 Date is required')
      .test('valid-date', 'Please enter a valid date (DD-MM-YYYY)', isValidDate),
    Weight1Time: Yup.string()
      .required('Weight 1 Time is required')
      .test('valid-time', 'Please enter a valid time (HH:MM)', isValidTime),
    Weight1DateTime: Yup.string().required('Weight 1 Date/Time is required'),
    Client: Yup.string().required('Client is required'),
    HaulierId: isEdit ? Yup.number().nullable() : Yup.number().required('Haulier ID is required'),
    Haulier: Yup.string().required('Haulier is required'),
    DriverId: Yup.number().required('Driver ID is required'),
    VehicleReg: Yup.string().required('Vehicle Registration is required'),
    Weight1SeqNo: Yup.number(),
    Weight1Value: Yup.number().required('Weight 1 Value is required'),
    Weight2SeqNo: Yup.number(),
    Weight2Value: Yup.number().required('Weight 2 Value is required'),
    Weight2Date: Yup.string()
      .required('Weight 2 Date is required')
      .test('valid-date', 'Please enter a valid date (DD-MM-YYYY)', isValidDate),
    Weight2Time: Yup.string()
      .required('Weight 2 Time is required')
      .test('valid-time', 'Please enter a valid time (HH:MM)', isValidTime),
    Weight2DateTime: Yup.string().required('Weight 2 Date/Time is required'),
    WeightNet: Yup.number().required('Net Weight is required'),
    SourceSiteName: Yup.string().required('Source Site Name is required'),
    ServiceId: Yup.number()
      .nullable()
      .notRequired()
      .transform((value, originalValue) => {
        // Convert string to number, return null if empty or invalid
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return null;
        }
        const num = Number(originalValue);
        return isNaN(num) ? null : num;
      }),
    ServiceDescription: Yup.string().required('Service Description is required'),
    BridgeIdentity: Yup.string().required('Bridge Identity is required'),
    QuoteNo: Yup.string().nullable().notRequired(),
    ProblemIndicated: Yup.boolean().required('Problem Indicated is required'),
    ProblemDetail: Yup.string().nullable(),
    GeneralNotes: Yup.string().nullable(),
    GeneralNotesAddedBy: Yup.string().nullable(),
    GeneralNotesAdded: Yup.string().optional(),
    ClientRef: Yup.string(),
    DriverName: Yup.string().required('Driver Name is required')
  });

  // Parse initial datetime values
  const weight1DateTime = parseDateTime(currentTransaction?.Weight1DateTime || '');
  const weight2DateTime = parseDateTime(currentTransaction?.Weight2DateTime || '');
  // const date = new Date().toISOString();

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      TicketNumber: currentTransaction?.TicketNumber || '',
      DriverName: currentTransaction?.DriverName || '',
      ClientId: currentTransaction?.ClientId || null,
      Client: currentTransaction?.Client || '',
      HaulierId: currentTransaction?.HaulierId || null,
      Haulier: currentTransaction?.Haulier || '',
      DriverId: currentTransaction?.DriverId || null,
      VehicleReg: currentTransaction?.VehicleReg || '',
      Weight1SeqNo: currentTransaction?.Weight1SeqNo || '',
      Weight1Value: currentTransaction?.Weight1Value || null,
      Weight1Date: weight1DateTime.date,
      Weight1Time: weight1DateTime.time,
      Weight1DateTime: currentTransaction?.Weight1DateTime || '',
      Weight2SeqNo: currentTransaction?.Weight2SeqNo || '',
      Weight2Value: currentTransaction?.Weight2Value || null,
      Weight2Date: weight2DateTime.date,
      Weight2Time: weight2DateTime.time,
      Weight2DateTime: currentTransaction?.Weight2DateTime || '',
      WeightNet: currentTransaction?.WeightNet || null,
      SourceSiteName: currentTransaction?.SourceSiteName || '',
      ServiceId: currentTransaction?.ServiceId || null,
      ServiceDescription: currentTransaction?.ServiceDescription || '',
      BridgeIdentity: currentTransaction?.BridgeIdentity || '',
      QuoteNo: currentTransaction?.QuoteNo || '',
      ProblemIndicated: currentTransaction?.ProblemIndicated || false,
      ProblemDetail: currentTransaction?.ProblemDetail || null,
      ClientRef: currentTransaction?.ClientRef || '',
      GeneralNotes: currentTransaction?.GeneralNotes || '',
      GeneralNotesAddedBy: currentTransaction?.GeneralNotesAddedBy || user?.firstName,
      GeneralNotesAdded: currentTransaction?.GeneralNotesAdded || currentDate
    },
    validationSchema: NewTransactionSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        enqueueSnackbar('Please correct the errors before submitting.', { variant: 'error' });
        setSubmitting(false);
        return;
      }

      try {
        const payload: any = { ...values };

        // Remove the separate date/time fields from payload
        delete payload?.Weight1Date;
        delete payload?.Weight1Time;
        delete payload?.Weight2Date;
        delete payload?.Weight2Time;

        // Add hardcoded values
        payload.CurrentStatus = 'Completed';
        payload.StatusId = 3;
        payload.SiteId2 = 1;
        payload.BridgeId2 = 1;
        payload.BridgeIdentity2 = currentTransaction?.BridgeIdentity || values.BridgeIdentity || '';

        if (!isEdit) {
          delete payload.TicketNumber;
        }

        if (isEdit) {
          await axios.put(`/rest/api/transactions/${currentTransaction.Id}`, payload);
          enqueueSnackbar('Transaction updated successfully', { variant: 'success' });
        } else {
          await axios.post(`/rest/api/transactions`, payload);
          enqueueSnackbar('Transaction created successfully', { variant: 'success' });
        }

        navigate('/dashboard/transactions');
      } catch (error) {
        console.error(error);
        enqueueSnackbar('An error occurred while submitting the form', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setFieldValue, values } =
    formik;

  const disableSubmit =
    !isEdit && (!formik.isValid || formik.isSubmitting || Object.keys(formik.errors).length > 0);

  const ticketTabs = useMemo<TicketTabConfig[]>(
    () => [
      {
        id: 'transaction-details',
        label: 'Transaction Details',
        content: (
          <TransactionDetailsTab
            formProps={{
              values,
              touched,
              errors,
              getFieldProps,
              setFieldValue,
              isEdit
            }}
            ticketInfoProps={{
              bridgeIdentity,
              drivers,
              vehicleReg,
              haulier,
              client
            }}
            problemProps={{
              problems
            }}
            actionButtonsProps={{
              isSubmitting,
              isEdit,
              disableSubmit,
              onClose: () => navigate('/dashboard/transactions'),
              copiedInfo: {
                copied: currentTransaction?.Copied,
                sourceId: currentTransaction?.CopiedSourceId
              }
            }}
          />
        )
      },
      {
        id: 'waste-items',
        label: `Waste Items (${wasteItems.length})`,
        content: (
          <WasteItemsTab
            items={wasteItems}
            transactionNetWeight={currentTransaction?.WeightNet || 0}
          />
        )
      },
      {
        id: 'notes',
        label: 'Notes & Activity',
        content: <TicketNotesTab notes={mockNotes} />
      }
    ],
    [
      values,
      touched,
      errors,
      getFieldProps,
      setFieldValue,
      isEdit,
      bridgeIdentity,
      drivers,
      vehicleReg,
      haulier,
      client,
      problems,
      isSubmitting,
      disableSubmit,
      currentTransaction,
      wasteItems,
      mockNotes,
      navigate,
      currentTransaction?.WeightNet
    ]
  );

  // Auto-calculate net weight
  useEffect(() => {
    const weight1 = values.Weight1Value || 0;
    const weight2 = values.Weight2Value || 0;
    const netWeight = Math.abs(weight1 - weight2);
    setFieldValue('WeightNet', netWeight);
  }, [values.Weight1Value, values.Weight2Value, setFieldValue]);

  // Handle Weight1 date/time combination
  useEffect(() => {
    const combined = combineDateTime(values.Weight1Date, values.Weight1Time);
    if (combined !== values.Weight1DateTime) {
      setFieldValue('Weight1DateTime', combined);
    }
  }, [values.Weight1Date, values.Weight1Time, setFieldValue]);

  // Handle Weight2 date/time combination
  useEffect(() => {
    const combined = combineDateTime(values.Weight2Date, values.Weight2Time);
    if (combined !== values.Weight2DateTime) {
      setFieldValue('Weight2DateTime', combined);
    }
  }, [values.Weight2Date, values.Weight2Time, setFieldValue]);

  // Fetch functions
  const fetchContacts = async () => {
    try {
      const response2 = await axios.get('/rest/api/contacts/dropdown?isClient=1');
      const response3 = await axios.get('/rest/api/contacts/dropdown?IsHaulier=1');

      const allClients = response2.data.data || [];
      const allHaulier = response3.data.data || [];

      setClient(
        allClients.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
      setHaulier(
        allHaulier.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchDrivers = useCallback(async () => {
    try {
      const response = await axios.get('/rest/api/driver/dropdown');
      const allDrivers = response.data.data || [];
      setDrivers(allDrivers.map((el: any) => ({ value: el.name, id: el.Id })));
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  }, []);

  const fetchProblemDescriptions = useCallback(async () => {
    try {
      const response = await axios.get('/rest/api/problem/info');
      const allProblems = response.data.data || [];
      setProblems(allProblems.map((el: any) => ({ value: el.ProblemDescription, id: el.Id })));
    } catch (error) {
      console.error('Error fetching problem descriptions:', error);
    }
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`/rest/api/vehicle-dropdown`);
      const allVehicleTypes = response.data.data || [];

      const formattedVehicles = allVehicleTypes
        .filter((el: any) => el && el.Id && el.RegistrationPlate && el.Description)
        .map((el: any) => ({
          id: el.Id,
          registrationPlate: el.RegistrationPlate.trim(),
          description: el.Description.trim()
        }));

      setVehicleReg(formattedVehicles);
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      setVehicleReg([]);
    }
  };

  const fetchBridgeIdentity = async () => {
    try {
      const response = await axios.get(`/rest/api/weighbridge/units-dropdown`);
      const allBridgeIdentity = response.data.data || [];
      setBridgeIdentity(allBridgeIdentity);
    } catch (error) {
      console.error('Error fetching bridge identity:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
    fetchDrivers();
    fetchProblemDescriptions();
    fetchVehicles();
    fetchBridgeIdentity();
  }, [fetchDrivers, fetchProblemDescriptions]);

  return (
    <FormikProvider value={formik}>
      <Form noValidate onSubmit={handleSubmit}>
        <TicketSummary summary={summaryData} />
        <TicketTabs tabs={ticketTabs} />
      </Form>
    </FormikProvider>
  );
}
